<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from '../../featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { EMPTY_PROSPECT_DATA } from 'dashboard/helper/chatDataExtractor';
import { isActiveElementTypeable } from 'shared/helpers/KeyboardHelpers';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ChatSuggestionChip from './ChatSuggestionChip.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});
const emit = defineEmits(['sent']);
const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();
const { currentAccount } = useAccount();

const showDialog = ref(false);
const isSubmitting = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat || {});
const activeDealId = computed(
  () => currentChat.value?.active_deal_id || currentChat.value?.activeDealId
);

const accountId = computed(() => store.getters.getCurrentAccountId);
const isFeatureEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.SALES_FUNNEL
  )
);

const currentUser = computed(() => store.getters.getCurrentUser);
const isAdmin = computed(() => currentUser.value?.role === 'administrator');
const hasPermission = computed(() => {
  return isAdmin.value || checkPermissions(['sales_funnel_view']);
});

const canShow = computed(() => isFeatureEnabled.value && hasPermission.value);

const plans = computed(() => {
  const customPlans = (
    currentAccount.value?.settings?.internet_plans || []
  ).map(p => ({
    id: p.id,
    name: `${p.name} ($${parseFloat(p.price).toFixed(2)})`,
    value: parseFloat(p.price),
  }));

  if (customPlans.length) {
    return [
      ...customPlans,
      { id: 'custom', name: t('SALES_FUNNEL.PLANS.CUSTOM'), value: null },
    ];
  }

  // Fallback to Nodexcom actual company plans
  return [
    { id: 'plan_400m', name: t('SALES_FUNNEL.PLANS.FIBER_400'), value: 30.0 },
    { id: 'plan_600m', name: t('SALES_FUNNEL.PLANS.FIBER_600'), value: 35.0 },
    { id: 'plan_800m', name: t('SALES_FUNNEL.PLANS.FIBER_800'), value: 40.0 },
    { id: 'plan_1000m', name: t('SALES_FUNNEL.PLANS.FIBER_1000'), value: 50.0 },
    { id: 'custom', name: t('SALES_FUNNEL.PLANS.CUSTOM'), value: null },
  ];
});

const selectedPlan = ref(plans.value[0]?.id || 'plan_400m');
const selectedStageId = ref(null);

const form = ref({
  firstName: '',
  lastName: '',
  value: (plans.value[0]?.value || 30.0).toString(),
  phoneNumber: '',
  email: '',
  gpsCoordinates: '',
  cedula: '',
  direccion: '',
  expectedCloseDate: '',
  notes: '',
  leadSource: '',
  assigneeId: null,
  custom_attributes: {},
});

const agents = computed(() => store.getters['agents/getAgents'] || []);

const leadSourceOptions = [
  'social_media',
  'referral',
  'phone',
  'field_sales',
  'office',
  'website',
  'paid_ads',
  'email',
  'other',
  'facebook',
  'instagram',
  'tiktok',
  'whatsapp',
  'youtube',
  'google',
];

const stages = computed(() => store.getters['salesFunnel/getStages'] || []);

const selectedStage = computed(() =>
  stages.value.find(s => String(s.id) === String(selectedStageId.value))
);

const requiredAttributes = computed(() => {
  const attrs = selectedStage.value?.required_attributes;
  if (!attrs) return [];
  if (Array.isArray(attrs)) return attrs;
  try {
    return JSON.parse(attrs);
  } catch (e) {
    return [];
  }
});

const isAttrRequired = key => {
  const attrs = requiredAttributes.value || [];
  if (key === 'email') {
    return (
      attrs.includes('email') ||
      attrs.includes('correo') ||
      attrs.includes('correo_electronico') ||
      attrs.includes('email_address')
    );
  }
  if (key === 'phone_number' || key === 'phone') {
    return attrs.includes('phone_number') || attrs.includes('phone');
  }
  if (key === 'gps_coordinates' || key === 'gps') {
    return attrs.includes('gps_coordinates') || attrs.includes('gps');
  }
  return attrs.includes(key);
};

const shouldShowValue = computed(
  () => isAttrRequired('value') || selectedPlan.value === 'custom'
);

const shouldShowCedula = computed(() => true);

const shouldShowDireccion = computed(() => true);

const shouldShowGPS = computed(
  () => isAttrRequired('gps_coordinates') || isAttrRequired('gps')
);

const shouldShowPhone = computed(
  () => isAttrRequired('phone_number') || isAttrRequired('phone')
);

const shouldShowEmail = computed(() => isAttrRequired('email'));

const shouldShowCloseDate = computed(() =>
  isAttrRequired('expected_close_date')
);

const shouldShowLeadSource = computed(() => isAttrRequired('lead_source'));

const shouldShowAssignee = computed(() => isAttrRequired('assignee_id'));

const shouldShowNotes = computed(() => isAttrRequired('notes'));

const customRequiredKeys = computed(() => {
  const standardKeys = [
    'cedula',
    'direccion',
    'gps_coordinates',
    'gps',
    'phone_number',
    'phone',
    'email',
    'correo',
    'correo_electronico',
    'email_address',
    'value',
    'expected_close_date',
    'internet_plan_id',
    'lead_source',
    'assignee_id',
    'notes',
  ];
  return requiredAttributes.value.filter(attr => !standardKeys.includes(attr));
});

const cedulaPrefix = ref('V');
const cedulaNumber = ref('');

function parseCedula(str) {
  if (!str) return { prefix: 'V', number: '' };
  const match = str.trim().match(/^([VEJGvejg])[-_.\s]?(.*)$/);
  if (match) {
    return { prefix: match[1].toUpperCase(), number: match[2].trim() };
  }
  return { prefix: 'V', number: str.trim() };
}

function formatCedula(prefix, number) {
  if (!number) return '';
  const cleanNumber = number.replace(/^[VEJGvejg][-_.\s]?/i, '').trim();
  return cleanNumber ? `${prefix}-${cleanNumber}` : '';
}

const cedulaLabel = computed(() => {
  const typeKey = cedulaPrefix.value || 'V';
  return t(`SALES_FUNNEL.CREATE_DEAL.CEDULA_TYPES.${typeKey}`);
});

const formattedAgents = computed(() => {
  const list = (agents.value || []).map(agent => ({
    id: agent.id,
    name:
      agent.name ||
      agent.available_name ||
      t('SALES_FUNNEL.CREATE_DEAL.ASSIGNEE_PLACEHOLDER'),
    thumbnail: agent.avatar_url || agent.thumbnail,
    availability_status: agent.availability_status || agent.status,
  }));
  return [
    { id: null, name: t('SALES_FUNNEL.CREATE_DEAL.ASSIGNEE_PLACEHOLDER') },
    ...list,
  ];
});

const selectedAgentItem = computed(() => {
  return (
    formattedAgents.value.find(a => a.id === form.value.assigneeId) || {
      id: null,
      name: t('SALES_FUNNEL.CREATE_DEAL.ASSIGNEE_PLACEHOLDER'),
    }
  );
});

const onSelectAgent = item => {
  form.value.assigneeId = item?.id || null;
};

const formattedLeadSources = computed(() => {
  return leadSourceOptions.map(src => ({
    id: src,
    name: t(`SALES_FUNNEL.LEAD_SOURCES.${src.toUpperCase()}`),
  }));
});

const selectedLeadSourceItem = computed(() => {
  return (
    formattedLeadSources.value.find(
      opt => opt.id === form.value.leadSource
    ) || {
      id: '',
      name: t('SALES_FUNNEL.PLACEHOLDERS.SELECT_LEAD_SOURCE'),
    }
  );
});

function onSelectLeadSource(item) {
  form.value.leadSource = item?.id || '';
}

function splitHispanicName(nameStr) {
  if (!nameStr) return { firstName: '', lastName: '' };
  const parts = nameStr.trim().split(/\s+/);
  if (parts.length >= 4) {
    return {
      firstName: parts.slice(0, 2).join(' '),
      lastName: parts.slice(2).join(' '),
    };
  }
  if (parts.length === 3) {
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
    };
  }
  if (parts.length === 2) {
    return {
      firstName: parts[0],
      lastName: parts[1],
    };
  }
  return {
    firstName: parts[0] || '',
    lastName: '',
  };
}

// Data spotted in the conversation, so the agent does not have to retype it.
const detected = ref({ ...EMPTY_PROSPECT_DATA });
const isScanning = ref(false);

function applyCedulaSuggestion() {
  const parsed = parseCedula(detected.value.cedula);
  cedulaPrefix.value = parsed.prefix;
  cedulaNumber.value = parsed.number;
}

async function scanConversationForProspectData() {
  isScanning.value = true;
  try {
    const { data } = await ConversationApi.extractData(props.conversationId);
    let extractedCedula = data.cedula || '';
    if (extractedCedula) {
      const parsedCed = parseCedula(extractedCedula);
      extractedCedula = formatCedula(parsedCed.prefix, parsedCed.number);
    }
    detected.value = {
      cedula: extractedCedula,
      email: data.correo || data.email || '',
      phoneNumber: data.telefono || '',
      gpsCoordinates: data.coordenadasGPS || '',
      direccion: data.direccion || '',
      firstName: data.nombre || '',
      lastName: data.apellido || '',
    };
  } catch (error) {
    // Keep whatever the already loaded messages revealed or empty
    detected.value = { ...EMPTY_PROSPECT_DATA };
  } finally {
    isScanning.value = false;
  }
}

async function openDialog() {
  showDialog.value = true;
  // Clear data detected for a previously opened chat before scanning this one,
  // so stale suggestions from another conversation never show.
  detected.value = { ...EMPTY_PROSPECT_DATA };
  try {
    await store.dispatch('salesFunnel/fetchStages');
    const openStages = stages.value.filter(s => s.stage_type === 'open');
    if (openStages.length) {
      const sorted = [...openStages].sort((a, b) => a.position - b.position);
      selectedStageId.value = sorted[0].id;
    } else if (stages.value.length) {
      selectedStageId.value = stages.value[0].id;
    }

    // Pre-fill existing contact data from currentChat
    const sender = currentChat.value?.meta?.sender || {};
    const cAttributes =
      sender.custom_attributes || sender.customAttributes || {};
    const { firstName, lastName } = splitHispanicName(sender.name || '');
    form.value.firstName =
      sender.first_name || sender.firstName || firstName || '';
    form.value.lastName = sender.last_name || sender.lastName || lastName || '';
    form.value.phoneNumber = sender.phone_number || '';
    form.value.email = sender.email || '';
    const parsedCedula = parseCedula(cAttributes.cedula || '');
    cedulaPrefix.value = parsedCedula.prefix;
    cedulaNumber.value = parsedCedula.number;
    form.value.direccion = cAttributes.direccion || '';
    form.value.gpsCoordinates = cAttributes.gps_coordinates || '';

    // Pre-load agents for assignee select
    await store.dispatch('agents/get');

    // Always scan when opening the dialog to get the latest messages
    await scanConversationForProspectData();

    form.value.gpsCoordinates =
      detected.value.gpsCoordinates || form.value.gpsCoordinates;
    form.value.email = detected.value.email || form.value.email;
    form.value.phoneNumber =
      detected.value.phoneNumber || form.value.phoneNumber;
    form.value.direccion = detected.value.direccion || form.value.direccion;
    form.value.firstName = detected.value.firstName || form.value.firstName;
    form.value.lastName = detected.value.lastName || form.value.lastName;

    if (!cedulaNumber.value && detected.value.cedula) {
      applyCedulaSuggestion();
    }
  } catch (error) {
    // handle error
  }
}

// The panel leaves the chat interactive, so there is no backdrop to click on:
// Escape and the close button are the way out.
function onKeydown(event) {
  if (event.key !== 'Escape') return;
  // Escape inside a field only leaves the field, so a filled form is never
  // discarded by accident.
  if (isActiveElementTypeable(event)) {
    event.target.blur();
    return;
  }
  showDialog.value = false;
}

watch(showDialog, isOpen => {
  if (isOpen) {
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('keydown', onKeydown);
  }
});

watch(
  () => props.conversationId,
  () => {
    detected.value = { ...EMPTY_PROSPECT_DATA };
  }
);

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

watch(selectedPlan, newPlan => {
  const plan = plans.value.find(p => p.id === newPlan);
  if (plan && plan.id !== 'custom') {
    form.value.value = plan.value.toString();
  } else if (plan && plan.id === 'custom') {
    form.value.value = '';
  }
});

async function handleSubmit() {
  isSubmitting.value = true;
  try {
    const sender = currentChat.value?.meta?.sender || {};
    const customAttrs = {
      ...(form.value.custom_attributes || {}),
      internet_plan_id: selectedPlan.value,
    };

    customRequiredKeys.value.forEach(key => {
      if (form.value.custom_attributes[key]) {
        customAttrs[key] = form.value.custom_attributes[key];
      }
    });

    const fullName = `${form.value.firstName} ${form.value.lastName}`.trim();
    const fullCedula = formatCedula(cedulaPrefix.value, cedulaNumber.value);
    const contactPayload = {
      name: fullName || sender.name || '',
      first_name: form.value.firstName,
      last_name: form.value.lastName,
      email: form.value.email || sender.email || '',
      phone_number: form.value.phoneNumber || sender.phone_number || '',
      custom_attributes: {
        ...(sender.custom_attributes || {}),
        ...(sender.customAttributes || {}),
        cedula: fullCedula,
        direccion: form.value.direccion,
      },
    };

    const dealPayload = {
      name: '',
      value: parseFloat(form.value.value) || 0,
      phone_number: form.value.phoneNumber || sender.phone_number || '',
      gps_coordinates: form.value.gpsCoordinates,
      funnel_stage_id: selectedStageId.value,
      expected_close_date: form.value.expectedCloseDate || null,
      notes: form.value.notes || '',
      custom_attributes: customAttrs,
    };

    if (form.value.leadSource) {
      dealPayload.lead_source = form.value.leadSource;
    }
    if (form.value.assigneeId) {
      dealPayload.assignee_id = form.value.assigneeId;
    }

    await store.dispatch('salesFunnel/createDeal', {
      deal: dealPayload,
      contact: contactPayload,
      conversation_id: props.conversationId,
    });

    // Re-fetch the conversation so active_deal_id updates reactively in the store
    await store.dispatch('getConversation', props.conversationId);
    showDialog.value = false;
    emit('sent');
  } catch (error) {
    let errorMessage = t('SALES_FUNNEL.CREATE_DEAL.ERROR_MESSAGE');
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message.replace(
        /^Validation failed:\s*/i,
        ''
      );
    }
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div v-if="canShow">
    <div
      v-if="activeDealId"
      class="flex items-center justify-center gap-2 w-full px-3 py-2 text-xs font-semibold rounded-lg text-n-teal-11 bg-n-teal-2 border border-n-teal-4"
    >
      <span class="i-lucide-id-card size-4" />
      {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.CLIENT_IN_FUNNEL') }}
    </div>
    <button
      v-else
      class="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-lg text-n-amber-11 bg-n-amber-2 hover:bg-n-amber-3 transition-colors"
      @click="openDialog"
    >
      <span class="i-lucide-funnel size-4" />
      {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.BUTTON') }}
    </button>

    <!-- Side panel: leaves the chat visible, scrollable and selectable -->
    <div
      v-if="showDialog"
      class="fixed inset-0 z-50 flex flex-col bg-white dark:bg-n-solid-2 shadow-2xl md:inset-y-0 md:left-auto md:right-0 md:w-[26rem] md:border-l md:border-n-weak"
    >
      <div
        class="flex items-start justify-between gap-2 px-5 py-4 border-b border-n-weak shrink-0"
      >
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.TITLE') }}
          </h3>
        </div>
        <button
          class="flex items-center justify-center size-7 rounded-lg hover:bg-n-alpha-2 shrink-0"
          :title="t('SALES_FUNNEL.SEND_TO_FUNNEL.CANCEL')"
          @click="showDialog = false"
        >
          <span class="i-lucide-x size-4 text-n-slate-10" />
        </button>
      </div>

      <form class="flex flex-col flex-1 min-h-0" @submit.prevent="handleSubmit">
        <div class="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          <!-- Lectura del historial del chat en curso -->
          <div
            v-if="isScanning"
            class="flex items-center gap-3 px-4 py-3 rounded-xl bg-n-alpha-1 border border-n-brand/20 shadow-sm relative overflow-hidden"
          >
            <!-- Subtle background pulse -->
            <div class="absolute inset-0 bg-n-brand/5 animate-pulse" />

            <div class="relative flex items-center justify-center size-10">
              <!-- Spinning border around the circle -->
              <div
                class="absolute inset-0 rounded-full border-2 border-n-brand/20 border-t-n-brand animate-spin"
              />

              <!-- Solid AI Circle -->
              <div
                class="relative flex items-center justify-center size-8 rounded-full bg-n-brand text-white shadow-md"
              >
                <span class="i-lucide-sparkles size-4" />
              </div>
            </div>

            <div class="relative flex flex-col">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.SCANNING') }}
              </span>
              <span class="text-[11px] font-medium text-n-slate-10">
                Extrayendo datos...
              </span>
            </div>
          </div>

          <!-- Etapa del Embudo -->
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.STAGE_LABEL') }}
              <span class="text-red-500">*</span>
            </label>
            <select
              v-model.number="selectedStageId"
              class="w-full pl-3 pr-6 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand appearance-none bg-origin-padding bg-[right_8px_center]"
              required
            >
              <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                {{ stage.name }}
              </option>
            </select>
          </div>

          <!-- Producto o Servicio / Plan -->
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.PLANS.SELECT') }}
            </label>
            <select
              v-model="selectedPlan"
              class="w-full pl-3 pr-6 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand appearance-none bg-origin-padding bg-[right_8px_center]"
            >
              <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                {{ plan.name }}
              </option>
            </select>
          </div>

          <!-- Valor / Monto (si plan es personalizado o la etapa lo exige) -->
          <div v-if="shouldShowValue">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.PLANS.VALUE_LABEL') }}
              <span v-if="isAttrRequired('value')" class="text-red-500">*</span>
            </label>
            <input
              v-model="form.value"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :required="isAttrRequired('value') || selectedPlan === 'custom'"
            />
          </div>

          <!-- Nombre y Apellido del Contacto -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-10 mb-1">
                {{ t('SALES_FUNNEL.CREATE_DEAL.CONTACT_NAME') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.firstName"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                required
                :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.FIRST_NAME')"
              />
              <ChatSuggestionChip
                :detected="detected.firstName"
                :current="form.firstName"
                @apply="form.firstName = detected.firstName"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-10 mb-1">
                {{ t('SALES_FUNNEL.CREATE_DEAL.CONTACT_LAST_NAME') }}
              </label>
              <input
                v-model="form.lastName"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.LAST_NAME')"
              />
              <ChatSuggestionChip
                :detected="detected.lastName"
                :current="form.lastName"
                @apply="form.lastName = detected.lastName"
              />
            </div>
          </div>

          <!-- Cédula / Identificación -->
          <div v-if="shouldShowCedula">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ cedulaLabel }}
              <span v-if="isAttrRequired('cedula')"
class="text-red-500"
                >*</span>
            </label>
            <div class="flex items-center gap-1.5">
              <select
                v-model="cedulaPrefix"
                class="w-16 px-2 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand shrink-0 text-center font-semibold cursor-pointer appearance-none bg-[right_4px_center]"
              >
                <option value="V">V</option>
                <option value="E">E</option>
                <option value="J">J</option>
                <option value="G">G</option>
              </select>
              <input
                v-model="cedulaNumber"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                :required="isAttrRequired('cedula')"
                :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.CEDULA')"
              />
            </div>
            <ChatSuggestionChip
              :detected="detected.cedula"
              :current="formatCedula(cedulaPrefix, cedulaNumber)"
              @apply="applyCedulaSuggestion"
            />
          </div>

          <!-- Dirección -->
          <div v-if="shouldShowDireccion">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.CREATE_DEAL.CONTACT_DIRECCION') }}
              <span v-if="isAttrRequired('direccion')"
class="text-red-500"
                >*</span>
            </label>
            <input
              v-model="form.direccion"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :required="isAttrRequired('direccion')"
              :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.DIRECCION')"
            />
            <ChatSuggestionChip
              :detected="detected.direccion"
              :current="form.direccion"
              @apply="form.direccion = detected.direccion"
            />
          </div>

          <!-- Coordenadas GPS -->
          <div v-if="shouldShowGPS">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.GPS') }}
              <span
                v-if="
                  isAttrRequired('gps_coordinates') || isAttrRequired('gps')
                "
                class="text-red-500"
                >*</span
              >
            </label>
            <input
              v-model="form.gpsCoordinates"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.GPS')"
              :required="
                isAttrRequired('gps_coordinates') || isAttrRequired('gps')
              "
            />
            <ChatSuggestionChip
              :detected="detected.gpsCoordinates"
              :current="form.gpsCoordinates"
              @apply="form.gpsCoordinates = detected.gpsCoordinates"
            />
          </div>

          <!-- Teléfono -->
          <div v-if="shouldShowPhone">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.CREATE_DEAL.CONTACT_PHONE') }}
              <span
                v-if="isAttrRequired('phone_number') || isAttrRequired('phone')"
                class="text-red-500"
                >*</span
              >
            </label>
            <input
              v-model="form.phoneNumber"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :required="
                isAttrRequired('phone_number') || isAttrRequired('phone')
              "
              :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.PHONE')"
            />
            <ChatSuggestionChip
              :detected="detected.phoneNumber"
              :current="form.phoneNumber"
              @apply="form.phoneNumber = detected.phoneNumber"
            />
          </div>

          <!-- Fecha Prevista de Cierre -->
          <div v-if="shouldShowCloseDate">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.DETAIL.EXPECTED_CLOSE_DATE') }}
              <span
                v-if="isAttrRequired('expected_close_date')"
                class="text-red-500"
                >*</span
              >
            </label>
            <input
              v-model="form.expectedCloseDate"
              type="date"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :required="isAttrRequired('expected_close_date')"
            />
          </div>

          <!-- Correo Electrónico -->
          <div v-if="shouldShowEmail">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.SETTINGS.STAGE_FIELDS.EMAIL') }}
              <span v-if="isAttrRequired('email')" class="text-red-500">*</span>
            </label>
            <input
              v-model="form.email"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :required="isAttrRequired('email')"
              :placeholder="t('SALES_FUNNEL.PLACEHOLDERS.EMAIL')"
            />
            <ChatSuggestionChip
              :detected="String(detected.email || '')"
              :current="String(form.email || '')"
              @apply="form.email = String(detected.email || '')"
            />
          </div>

          <!-- Origen del Prospecto -->
          <div v-if="shouldShowLeadSource">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.LEAD_SOURCES.LABEL') }}
              <span v-if="isAttrRequired('lead_source')"
class="text-red-500"
                >*</span>
            </label>
            <MultiselectDropdown
              :has-thumbnail="false"
              :options="formattedLeadSources"
              :selected-item="selectedLeadSourceItem"
              :multiselector-title="t('SALES_FUNNEL.LEAD_SOURCES.LABEL')"
              :multiselector-placeholder="
                t('SALES_FUNNEL.PLACEHOLDERS.SELECT_LEAD_SOURCE')
              "
              :input-placeholder="
                t('SALES_FUNNEL.PLACEHOLDERS.SEARCH_LEAD_SOURCE')
              "
              @select="onSelectLeadSource"
            />
          </div>

          <!-- Agente Asignado -->
          <div v-if="shouldShowAssignee">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.SETTINGS.STAGE_FIELDS.ASSIGNEE') }}
              <span v-if="isAttrRequired('assignee_id')"
class="text-red-500"
                >*</span>
            </label>
            <MultiselectDropdown
              :options="formattedAgents"
              :selected-item="selectedAgentItem"
              :multiselector-title="t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
              :multiselector-placeholder="
                t('SALES_FUNNEL.CREATE_DEAL.ASSIGNEE_PLACEHOLDER')
              "
              :no-search-result="
                t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
              "
              :input-placeholder="
                t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
              "
              @select="onSelectAgent"
            />
          </div>

          <!-- Notas -->
          <div v-if="shouldShowNotes">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ t('SALES_FUNNEL.SETTINGS.STAGE_FIELDS.NOTES') }}
              <span v-if="isAttrRequired('notes')" class="text-red-500">*</span>
            </label>
            <textarea
              v-model="form.notes"
              rows="2"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
              :required="isAttrRequired('notes')"
            />
          </div>

          <!-- Atributos Requeridos Personalizados Extras -->
          <div v-for="attrKey in customRequiredKeys" :key="attrKey">
            <label
              class="block text-xs font-medium text-n-slate-10 mb-1 capitalize"
            >
              {{ attrKey }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.custom_attributes[attrKey]"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              required
            />
          </div>
        </div>

        <div
          class="flex justify-end gap-3 px-5 py-4 border-t border-n-weak shrink-0"
        >
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="showDialog = false"
          >
            {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.CANCEL') }}
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand-dark disabled:opacity-50"
            :disabled="isSubmitting"
          >
            {{ t('SALES_FUNNEL.SEND_TO_FUNNEL.SUBMIT') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
